/**
 * Verification status badge helper
 *
 * Centralizes the rendering attributes (label / tooltip / aria-label / className /
 * inlineStyle) for the verification_status badge shown in Dashboard.js next to
 * the AI 判定 chip.
 *
 * Per 太子裁定 plan v3 (1-6 + a/b/c + N1/N2/N3 + 追加条件 1/2):
 * - 6 case (4 legacy + 2 new): verified / majority_verified / disagreement /
 *   single_source / primary_verified / fallback_verified
 * - role-aware: primary_verified / fallback_verified の label を学生 / 教師で切替
 *   (legacy 4 case は role 不変)
 * - tooltip / aria-label からモデル名 (Claude / OpenAI / Arbiter) prefix を完全削除
 * - aria-label = tooltip 文言 (太子裁定 N2: visual label を SR が既に読むため、
 *   aria-label に label を含めない)
 * - 既存 4 case の className / inlineStyle は完全保持
 * - 新 2 case (primary_verified / fallback_verified) は緑系 success 色 (CUD セーフ)
 */

/**
 * Get visual content for the verification status badge.
 *
 * @param {string|undefined} status - verification_status value
 * @param {boolean} isTeacherMode - currentUser.role === 'teacher'
 * @param {(key: string) => string} t - react-i18next translation function
 * @returns {{
 *   shouldRender: boolean,
 *   label: string,
 *   tooltip: string,
 *   ariaLabel: string,
 *   className: string,
 *   inlineStyle: { background?: string, color?: string }
 * }}
 */
export function getVerificationBadgeContent(status, isTeacherMode, t) {
  const empty = {
    shouldRender: false,
    label: '',
    tooltip: '',
    ariaLabel: '',
    className: '',
    inlineStyle: {},
  };

  if (!status) {
    return empty;
  }

  switch (status) {
    case 'verified': {
      const tooltip = t('verification.verified.tooltip');
      return {
        shouldRender: true,
        label: t('verification.verified.label'),
        tooltip,
        ariaLabel: tooltip,
        className: 'rounded-full px-2 py-0.5 font-semibold',
        inlineStyle: { background: 'rgba(47,107,62,0.1)', color: 'var(--success)' },
      };
    }

    case 'majority_verified': {
      const tooltip = t('verification.majorityVerified.tooltip');
      return {
        shouldRender: true,
        label: t('verification.majorityVerified.label'),
        tooltip,
        ariaLabel: tooltip,
        className: 'rounded-full px-2 py-0.5 font-semibold',
        inlineStyle: { background: 'rgba(47,107,62,0.1)', color: 'var(--success)' },
      };
    }

    case 'disagreement': {
      const tooltip = t('verification.disagreement.tooltip');
      return {
        shouldRender: true,
        label: t('verification.disagreement.label'),
        tooltip,
        ariaLabel: tooltip,
        className: 'rounded-full px-2 py-0.5 font-semibold',
        inlineStyle: { background: 'var(--warm-bg)', color: 'var(--warning)' },
      };
    }

    case 'single_source': {
      const tooltip = t('verification.singleSource.tooltip');
      return {
        shouldRender: true,
        label: t('verification.singleSource.label'),
        tooltip,
        ariaLabel: tooltip,
        className: 'rounded-full bg-[var(--bg-muted)] px-2 py-0.5 font-semibold text-[var(--text-muted)]',
        inlineStyle: {},
      };
    }

    case 'primary_verified': {
      const tooltip = t('verification.primaryVerified.tooltip');
      return {
        shouldRender: true,
        label: isTeacherMode ? t('verification.primaryVerified.labelTeacher') : t('verification.primaryVerified.labelStudent'),
        tooltip,
        ariaLabel: tooltip,
        className: 'rounded-full px-2 py-0.5 font-semibold',
        inlineStyle: { background: 'rgba(47,107,62,0.1)', color: 'var(--success)' },
      };
    }

    case 'fallback_verified': {
      const tooltip = t('verification.fallbackVerified.tooltip');
      return {
        shouldRender: true,
        label: isTeacherMode ? t('verification.fallbackVerified.labelTeacher') : t('verification.fallbackVerified.labelStudent'),
        tooltip,
        ariaLabel: tooltip,
        className: 'rounded-full px-2 py-0.5 font-semibold',
        inlineStyle: { background: 'rgba(47,107,62,0.06)', color: 'var(--success)' },
      };
    }

    default:
      return empty;
  }
}
